import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type {
  ActivityTerminalResult,
  ControlPlaneManagementResponse,
  ControlPlaneOnboardingStateResponse,
  ControlPlaneSkillPlanProjection,
  OnboardingPairingCreatedResponse,
  OnboardingPairingSummary,
  OnboardingPublicError,
  OnboardingPublicErrorCode,
  OnboardingSessionSummary,
  PairingPurpose,
  ResumeOnboardingAttemptResponse,
  UsageRecoveryAuthorizationResult,
} from '@internal/spassu-code-contract';
import {
  decodeControlPlaneManagementResponse,
  decodeControlPlaneSkillPlanProjection,
  decodeOnboardingPublicError,
  decodeResumeOnboardingAttemptResponse,
} from './controlPlaneManagementDecoder';

export interface ControlPlaneApi {
  /**
   * Canonical operational projection: activity cycle, connection, workspace and
   * server-resolved capabilities. The UI never derives expiry or authorization
   * from anything else (ADR-009).
   */
  getManagement(
    activityId: string,
    signal?: AbortSignal,
  ): Promise<ControlPlaneManagementResponse>;
  getOnboardingState(
    activityId: string,
    signal?: AbortSignal,
  ): Promise<ControlPlaneOnboardingStateResponse>;
  getSkillPlan(
    activityId: string,
    signal?: AbortSignal,
  ): Promise<ControlPlaneSkillPlanProjection>;
  /**
   * `purpose` distinguishes the first pairing from renewal, recovery and
   * cleanup enrollment. The response carries pairing material exactly once and is
   * never cached (ADR-010).
   */
  createOnboardingPairing(
    activityId: string,
    idempotencyKey: string,
    purpose?: PairingPurpose,
    signal?: AbortSignal,
  ): Promise<OnboardingPairingCreatedResponse | OnboardingPairingSummary>;
  resumeOnboardingAttempt(
    activityId: string,
    sessionId: string,
    idempotencyKey: string,
    signal?: AbortSignal,
  ): Promise<ResumeOnboardingAttemptResponse>;
  finalizeActivity(
    activityId: string,
    idempotencyKey: string,
    signal?: AbortSignal,
  ): Promise<ActivityTerminalResult>;
  cancelActivity(
    activityId: string,
    reason: string,
    idempotencyKey: string,
    signal?: AbortSignal,
  ): Promise<ActivityTerminalResult>;
  authorizeUsageRecovery(
    activityId: string,
    signal?: AbortSignal,
  ): Promise<UsageRecoveryAuthorizationResult>;
  getOnboardingSession(
    activityId: string,
    sessionId: string,
    signal?: AbortSignal,
  ): Promise<OnboardingSessionSummary>;
  revokeOnboardingSession(
    activityId: string,
    sessionId: string,
    idempotencyKey: string,
    signal?: AbortSignal,
  ): Promise<OnboardingSessionSummary>;
}

export const controlPlaneApiRef = createApiRef<ControlPlaneApi>({
  id: 'plugin.spassu-code-control-plane.api',
});

export class ControlPlaneApiError extends Error {
  readonly statusCode: number;
  readonly code?: OnboardingPublicErrorCode;
  readonly action?: string;

  constructor(
    statusCode: number,
    fallback: string,
    body?: Partial<OnboardingPublicError>,
  ) {
    super(body?.message || fallback);
    this.name = 'ControlPlaneApiError';
    this.statusCode = statusCode;
    this.code = body?.code;
    this.action = body?.action;
  }
}

export class ControlPlaneClient implements ControlPlaneApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async request<T>(
    path: string,
    fallback: string,
    init: RequestInit = {},
    decode?: (value: unknown) => T,
  ): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('spassu-code');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, init);
    if (response.ok) {
      const body: unknown = await response.json();
      return decode ? decode(body) : (body as T);
    }
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
    throw new ControlPlaneApiError(
      response.status,
      fallback,
      decodeOnboardingPublicError(body),
    );
  }

  getManagement(activityId: string, signal?: AbortSignal) {
    return this.request<ControlPlaneManagementResponse>(
      `/control-plane/activities/${encodeURIComponent(activityId)}/management`,
      'Não foi possível carregar a gestão da conexão.',
      { cache: 'no-store', signal },
      decodeControlPlaneManagementResponse,
    );
  }

  finalizeActivity(
    activityId: string,
    idempotencyKey: string,
    signal?: AbortSignal,
  ) {
    return this.request<ActivityTerminalResult>(
      `/control-plane/activities/${encodeURIComponent(activityId)}/finalize`,
      'Não foi possível concluir a atividade.',
      this.postInit(idempotencyKey, undefined, signal),
    );
  }

  cancelActivity(
    activityId: string,
    reason: string,
    idempotencyKey: string,
    signal?: AbortSignal,
  ) {
    return this.request<ActivityTerminalResult>(
      `/control-plane/activities/${encodeURIComponent(activityId)}/cancel`,
      'Não foi possível cancelar a atividade.',
      this.postInit(idempotencyKey, { reason }, signal),
    );
  }

  authorizeUsageRecovery(activityId: string, signal?: AbortSignal) {
    return this.request<UsageRecoveryAuthorizationResult>(
      `/control-plane/activities/${encodeURIComponent(
        activityId,
      )}/usage-recoveries`,
      'Não foi possível autorizar a recuperação de tokens.',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
        signal,
      },
    );
  }

  getOnboardingState(activityId: string, signal?: AbortSignal) {
    return this.request<ControlPlaneOnboardingStateResponse>(
      `/control-plane/activities/${encodeURIComponent(activityId)}/onboarding`,
      'Não foi possível carregar a conexão da atividade.',
      { cache: 'no-store', signal },
    );
  }

  getSkillPlan(activityId: string, signal?: AbortSignal) {
    return this.request<ControlPlaneSkillPlanProjection>(
      `/control-plane/activities/${encodeURIComponent(activityId)}/skill-plan`,
      'Não foi possível carregar as skills da atividade.',
      { cache: 'no-store', signal },
      decodeControlPlaneSkillPlanProjection,
    );
  }

  createOnboardingPairing(
    activityId: string,
    idempotencyKey: string,
    purpose?: PairingPurpose,
    signal?: AbortSignal,
  ) {
    return this.request<
      OnboardingPairingCreatedResponse | OnboardingPairingSummary
    >(
      `/control-plane/activities/${encodeURIComponent(
        activityId,
      )}/onboarding-pairings`,
      'Não foi possível gerar o código de pareamento.',
      this.postInit(idempotencyKey, purpose ? { purpose } : undefined, signal),
    );
  }

  resumeOnboardingAttempt(
    activityId: string,
    sessionId: string,
    idempotencyKey: string,
    signal?: AbortSignal,
  ) {
    return this.request<ResumeOnboardingAttemptResponse>(
      `/control-plane/activities/${encodeURIComponent(
        activityId,
      )}/onboarding-attempts/${encodeURIComponent(sessionId)}/resume`,
      'Não foi possível retomar a conexão.',
      {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
        signal,
      },
      decodeResumeOnboardingAttemptResponse,
    );
  }

  getOnboardingSession(
    activityId: string,
    sessionId: string,
    signal?: AbortSignal,
  ) {
    return this.request<OnboardingSessionSummary>(
      this.sessionPath(activityId, sessionId),
      'Não foi possível atualizar a sessão.',
      { cache: 'no-store', signal },
    );
  }

  revokeOnboardingSession(
    activityId: string,
    sessionId: string,
    idempotencyKey: string,
    signal?: AbortSignal,
  ) {
    return this.request<OnboardingSessionSummary>(
      `${this.sessionPath(activityId, sessionId)}/revoke`,
      'Não foi possível revogar a credencial.',
      this.postInit(
        idempotencyKey,
        { reason: 'owner-requested-revocation' },
        signal,
      ),
    );
  }

  private sessionPath(activityId: string, sessionId: string): string {
    return `/control-plane/activities/${encodeURIComponent(
      activityId,
    )}/onboarding-sessions/${encodeURIComponent(sessionId)}`;
  }

  private postInit(
    idempotencyKey: string,
    body: Record<string, unknown> | undefined,
    signal?: AbortSignal,
  ): RequestInit {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
      },
      body: JSON.stringify(body ?? {}),
      signal,
    };
  }
}
